import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  demoInventoryItems,
  demoStockTransactions,
  demoVendors,
  demoPurchaseOrders,
  demoGoodsReceivedNotes,
} from '@/mocks/demoData';
import type {
  InventoryItem, StockTransaction, Vendor,
  PurchaseOrder, PurchaseOrderItem, GoodsReceivedNote,
} from '@/mocks/data';
import { addPurchaseBill } from '@/utils/purchaseBillStore';

export interface DepartmentStock {
  dept: string;
  itemId: string;
  qty: number;
  parLevel: number;
}

export interface PendingItem {
  itemId: string;
  itemName: string;
  unit: string;
  vendorId: string;
  vendorName: string;
  poNumber: string;
  poId: string;
  qtyOrdered: number;
  qtyReceived: number;
  qtyPending: number;
}

export interface StockLedgerEntry {
  date: string;
  ref: string;
  type: string;
  qty: number;
  balance: number;
  details: string;
}

interface InventoryContextType {
  items: InventoryItem[];
  transactions: StockTransaction[];
  vendors: Vendor[];
  purchaseOrders: PurchaseOrder[];
  goodsReceivedNotes: GoodsReceivedNote[];
  deptStocks: DepartmentStock[];

  setItems: (items: InventoryItem[]) => void;
  addItem: (item: InventoryItem) => void;
  updateItem: (id: string, updates: Partial<InventoryItem>) => void;
  deleteItem: (id: string) => void;
  addTransaction: (tx: StockTransaction) => void;
  addVendor: (vendor: Vendor) => void;
  updateVendor: (id: string, updates: Partial<Vendor>) => void;
  deleteVendor: (id: string) => void;

  stockIn: (itemId: string, qty: number, vendorId: string, billNo: string, price: number) => void;
  stockOut: (itemId: string, qty: number, dept: string, reason?: string) => void;
  stockAdjust: (itemId: string, qty: number, reason: string) => void;
  transferToDept: (itemId: string, qty: number, dept: string) => void;
  setDeptParLevel: (dept: string, itemId: string, parLevel: number) => void;

  createPurchaseOrder: (po: Omit<PurchaseOrder, 'id' | 'created_at'>) => void;
  receiveGoods: (grn: Omit<GoodsReceivedNote, 'id' | 'created_at'>) => void;
  cancelPO: (poId: string) => void;

  lowStockItems: InventoryItem[];
  lowStockDepts: { dept: string; itemId: string; itemName: string; current: number; par: number }[];
  getDeptStock: (dept: string, itemId: string) => number;
  getItemTransactions: (itemId: string) => StockTransaction[];

  getPendingByVendor: (vendorId: string) => PendingItem[];
  getAllPendingItems: () => PendingItem[];
  getStockLedger: (itemId: string) => StockLedgerEntry[];
  getVendorGRNHistory: (vendorId: string) => GoodsReceivedNote[];
  getPOById: (poId: string) => PurchaseOrder | undefined;
}

export const DEPARTMENTS = ['Housekeeping', 'Kitchen', 'Maintenance', 'Reception'];

const InventoryContext = createContext<InventoryContextType | null>(null);

export function InventoryProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<InventoryItem[]>(demoInventoryItems);
  const [transactions, setTransactions] = useState<StockTransaction[]>(demoStockTransactions);
  const [vendors, setVendors] = useState<Vendor[]>(demoVendors);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>(demoPurchaseOrders);
  const [goodsReceivedNotes, setGoodsReceivedNotes] = useState<GoodsReceivedNote[]>(demoGoodsReceivedNotes);

  const [deptStocks, setDeptStocks] = useState<DepartmentStock[]>(() => {
    const initial: DepartmentStock[] = [];
    DEPARTMENTS.forEach((dept) => {
      demoInventoryItems.forEach((item) => {
        const deptPar = dept === 'Housekeeping' ? Math.max(1, Math.round(item.min_stock_level * 0.3)) : 0;
        initial.push({ dept, itemId: item.id, qty: 0, parLevel: deptPar });
      });
    });
    return initial;
  });

  const addItem = useCallback((item: InventoryItem) => {
    setItems((prev) => [...prev, item]);
    setDeptStocks((prev) => {
      const newStocks: DepartmentStock[] = [];
      DEPARTMENTS.forEach((dept) => {
        newStocks.push({ dept, itemId: item.id, qty: 0, parLevel: Math.round(item.min_stock_level * 0.3) });
      });
      return [...prev, ...newStocks];
    });
  }, []);

  const updateItem = useCallback((id: string, updates: Partial<InventoryItem>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...updates } : i)));
  }, []);

  const deleteItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    setDeptStocks((prev) => prev.filter((ds) => ds.itemId !== id));
  }, []);

  const addTransaction = useCallback((tx: StockTransaction) => {
    setTransactions((prev) => [{ ...tx, created_at: tx.created_at || new Date().toISOString() }, ...prev]);
  }, []);

  const addVendor = useCallback((vendor: Vendor) => {
    setVendors((prev) => [...prev, { ...vendor, created_at: vendor.created_at || new Date().toISOString() }]);
  }, []);

  const updateVendor = useCallback((id: string, updates: Partial<Vendor>) => {
    setVendors((prev) => prev.map((v) => (v.id === id ? { ...v, ...updates } : v)));
  }, []);

  const deleteVendor = useCallback((id: string) => {
    setVendors((prev) => prev.filter((v) => v.id !== id));
  }, []);

  const stockIn = useCallback((itemId: string, qty: number, vendorId: string, billNo: string, price: number) => {
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, current_qty: i.current_qty + qty } : i)));
    const tx: StockTransaction = {
      id: `stx-${Date.now()}`,
      item_id: itemId,
      type: 'purchase',
      qty,
      vendor_id: vendorId,
      bill_no: billNo,
      price,
      created_at: new Date().toISOString(),
    };
    setTransactions((prev) => [tx, ...prev]);
  }, []);

  const stockOut = useCallback((itemId: string, qty: number, dept: string, reason?: string) => {
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, current_qty: Math.max(0, i.current_qty - qty) } : i)));
    setDeptStocks((prev) => prev.map((ds) => {
      if (ds.dept === dept && ds.itemId === itemId) {
        return { ...ds, qty: Math.max(0, ds.qty - qty) };
      }
      return ds;
    }));
    const tx: StockTransaction = {
      id: `stx-${Date.now()}`,
      item_id: itemId,
      type: 'usage',
      qty,
      dept,
      reason: reason || '',
      created_at: new Date().toISOString(),
    };
    setTransactions((prev) => [tx, ...prev]);
  }, []);

  const stockAdjust = useCallback((itemId: string, qty: number, reason: string) => {
    setItems((prev) => prev.map((i) => {
      if (i.id === itemId) {
        const newQty = Math.max(0, i.current_qty + qty);
        return { ...i, current_qty: newQty };
      }
      return i;
    }));
    const tx: StockTransaction = {
      id: `stx-${Date.now()}`,
      item_id: itemId,
      type: 'adjustment',
      qty,
      reason,
      created_at: new Date().toISOString(),
    };
    setTransactions((prev) => [tx, ...prev]);
  }, []);

  const transferToDept = useCallback((itemId: string, qty: number, dept: string) => {
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, current_qty: Math.max(0, i.current_qty - qty) } : i)));
    setDeptStocks((prev) => prev.map((ds) => {
      if (ds.dept === dept && ds.itemId === itemId) {
        return { ...ds, qty: ds.qty + qty };
      }
      return ds;
    }));
    const tx: StockTransaction = {
      id: `stx-${Date.now()}`,
      item_id: itemId,
      type: 'usage',
      qty,
      dept: `Transfer to ${dept}`,
      reason: 'Department refill from Main Store',
      created_at: new Date().toISOString(),
    };
    setTransactions((prev) => [tx, ...prev]);
  }, []);

  const setDeptParLevel = useCallback((dept: string, itemId: string, parLevel: number) => {
    setDeptStocks((prev) => {
      const exists = prev.some((ds) => ds.dept === dept && ds.itemId === itemId);
      if (exists) {
        return prev.map((ds) => (ds.dept === dept && ds.itemId === itemId ? { ...ds, parLevel } : ds));
      }
      return [...prev, { dept, itemId, qty: 0, parLevel }];
    });
  }, []);

  const createPurchaseOrder = useCallback((po: Omit<PurchaseOrder, 'id' | 'created_at'>) => {
    const newPO: PurchaseOrder = {
      ...po,
      id: `po-${Date.now()}`,
      created_at: new Date().toISOString(),
    };
    setPurchaseOrders((prev) => [...prev, newPO]);
  }, []);

  const receiveGoods = useCallback((grn: Omit<GoodsReceivedNote, 'id' | 'created_at'>) => {
    const newGRN: GoodsReceivedNote = {
      ...grn,
      id: `grn-${Date.now()}`,
      created_at: new Date().toISOString(),
    };
    setGoodsReceivedNotes((prev) => [...prev, newGRN]);

    setPurchaseOrders((prev) => prev.map((po) => {
      if (po.id !== grn.po_id) return po;
      const updatedItems: PurchaseOrderItem[] = po.items.map((poi) => {
        const grnItem = grn.items.find((gi) => gi.item_id === poi.item_id);
        if (grnItem) {
          return { ...poi, qty_received: poi.qty_received + grnItem.qty_received };
        }
        return poi;
      });
      const allReceived = updatedItems.every((poi) => poi.qty_received >= poi.qty_ordered);
      const anyReceived = updatedItems.some((poi) => poi.qty_received > 0);
      let status: 'ordered' | 'partial' | 'received' = 'ordered';
      if (allReceived) status = 'received';
      else if (anyReceived) status = 'partial';
      return { ...po, items: updatedItems, status };
    }));

    setItems((prev) => prev.map((item) => {
      const grnItem = grn.items.find((gi) => gi.item_id === item.id);
      if (grnItem) {
        return { ...item, current_qty: item.current_qty + grnItem.qty_received };
      }
      return item;
    }));

    grn.items.forEach((grnItem) => {
      const tx: StockTransaction = {
        id: `stx-grn-${Date.now()}-${grnItem.item_id}`,
        item_id: grnItem.item_id,
        type: 'purchase',
        qty: grnItem.qty_received,
        vendor_id: grn.vendor_id,
        bill_no: grn.bill_no,
        price: grn.bill_amount,
        created_at: grn.received_date || new Date().toISOString(),
      };
      setTransactions((prev) => [tx, ...prev]);
    });
  }, []);

  const cancelPO = useCallback((poId: string) => {
    setPurchaseOrders((prev) => prev.map((po) => {
      if (po.id === poId && (po.status === 'ordered' || po.status === 'partial')) {
        return { ...po, status: 'cancelled' };
      }
      return po;
    }));
  }, []);

  const getPendingByVendor = useCallback((vendorId: string): PendingItem[] => {
    const result: PendingItem[] = [];
    purchaseOrders
      .filter((po) => po.vendor_id === vendorId && po.status !== 'cancelled' && po.status !== 'received')
      .forEach((po) => {
        po.items.forEach((poi) => {
          if (poi.qty_received < poi.qty_ordered) {
            const item = items.find((i) => i.id === poi.item_id);
            const vendor = vendors.find((v) => v.id === vendorId);
            result.push({
              itemId: poi.item_id,
              itemName: item?.name || 'Unknown',
              unit: item?.unit || '',
              vendorId,
              vendorName: vendor?.name || 'Unknown',
              poNumber: po.po_number,
              poId: po.id,
              qtyOrdered: poi.qty_ordered,
              qtyReceived: poi.qty_received,
              qtyPending: poi.qty_ordered - poi.qty_received,
            });
          }
        });
      });
    return result;
  }, [purchaseOrders, items, vendors]);

  const getAllPendingItems = useCallback((): PendingItem[] => {
    const result: PendingItem[] = [];
    purchaseOrders
      .filter((po) => po.status !== 'cancelled' && po.status !== 'received')
      .forEach((po) => {
        po.items.forEach((poi) => {
          if (poi.qty_received < poi.qty_ordered) {
            const item = items.find((i) => i.id === poi.item_id);
            const vendor = vendors.find((v) => v.id === po.vendor_id);
            result.push({
              itemId: poi.item_id,
              itemName: item?.name || 'Unknown',
              unit: item?.unit || '',
              vendorId: po.vendor_id,
              vendorName: vendor?.name || 'Unknown',
              poNumber: po.po_number,
              poId: po.id,
              qtyOrdered: poi.qty_ordered,
              qtyReceived: poi.qty_received,
              qtyPending: poi.qty_ordered - poi.qty_received,
            });
          }
        });
      });
    return result;
  }, [purchaseOrders, items, vendors]);

  const getStockLedger = useCallback((itemId: string): StockLedgerEntry[] => {
    const itemTxs = transactions
      .filter((t) => t.item_id === itemId)
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

    let balance = 0;
    const entries: StockLedgerEntry[] = [];

    itemTxs.forEach((t) => {
      const isPlus = t.type === 'purchase' || (t.type === 'adjustment' && t.qty > 0);
      const qty = isPlus ? t.qty : -t.qty;
      balance += qty;

      let typeLabel = '';
      let details = '';
      let ref = '';

      if (t.type === 'purchase') {
        const po = purchaseOrders.find((p) => p.items.some((pi) => pi.item_id === itemId) && p.vendor_id === t.vendor_id);
        ref = po?.po_number || t.bill_no || '';
        typeLabel = 'GRN / Stock In';
        const vendor = vendors.find((v) => v.id === t.vendor_id);
        details = `Received from ${vendor?.name || 'Vendor'}${t.bill_no ? ` | Bill: ${t.bill_no}` : ''}`;
      } else if (t.type === 'usage') {
        typeLabel = 'Stock Out / Usage';
        details = `${t.dept || ''}${t.reason ? ` — ${t.reason}` : ''}`;
      } else if (t.type === 'adjustment') {
        typeLabel = 'Adjustment';
        details = t.reason || 'Manual adjustment';
      }

      entries.push({
        date: new Date(t.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        ref,
        type: typeLabel,
        qty,
        balance,
        details,
      });
    });

    return entries;
  }, [transactions, purchaseOrders, vendors]);

  const getVendorGRNHistory = useCallback((vendorId: string): GoodsReceivedNote[] => {
    return goodsReceivedNotes.filter((grn) => grn.vendor_id === vendorId);
  }, [goodsReceivedNotes]);

  const getPOById = useCallback((poId: string): PurchaseOrder | undefined => {
    return purchaseOrders.find((po) => po.id === poId);
  }, [purchaseOrders]);

  const lowStockItems = useMemo(() => {
    return items.filter((i) => i.current_qty < i.min_stock_level);
  }, [items]);

  const lowStockDepts = useMemo(() => {
    return deptStocks
      .filter((ds) => ds.parLevel > 0 && ds.qty < ds.parLevel)
      .map((ds) => {
        const item = items.find((i) => i.id === ds.itemId);
        return { dept: ds.dept, itemId: ds.itemId, itemName: item?.name || 'Unknown', current: ds.qty, par: ds.parLevel };
      });
  }, [deptStocks, items]);

  const getDeptStock = useCallback((dept: string, itemId: string): number => {
    const ds = deptStocks.find((d) => d.dept === dept && d.itemId === itemId);
    return ds ? ds.qty : 0;
  }, [deptStocks]);

  const getItemTransactions = useCallback((itemId: string): StockTransaction[] => {
    return transactions.filter((t) => t.item_id === itemId);
  }, [transactions]);

  return (
    <InventoryContext.Provider
      value={{
        items, transactions, vendors, purchaseOrders, goodsReceivedNotes, deptStocks,
        setItems, addItem, updateItem, deleteItem,
        addTransaction, addVendor, updateVendor, deleteVendor,
        stockIn, stockOut, stockAdjust, transferToDept, setDeptParLevel,
        createPurchaseOrder, receiveGoods, cancelPO,
        lowStockItems, lowStockDepts, getDeptStock, getItemTransactions,
        getPendingByVendor, getAllPendingItems, getStockLedger,
        getVendorGRNHistory, getPOById,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
}

export function useInventory() {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error('useInventory must be used within InventoryProvider');
  return ctx;
}

